import React, { useCallback, useEffect, useState } from 'react';
import { useGetAllWorkletsForParentSpecQuery, useGetCustomStages } from '@frontend/app/hooks';
import { useGetCreatorTasksConfig } from '@frontend/app/hooks/useGetCreatorTasksConfig';
import { GetAllWorkletsForParentSpecQuery_worklets } from '@frontend/app/queries/types/GetAllWorkletsForParentSpecQuery';
import { ConsolidatedWorkflowSection } from '../sections/ConsolidatedWorkflowSection';
import { useInitializeCampaign } from '../hooks/useInitializeCampaign';
import { TProject, TWorklet } from '../../types';
import { CreatorPortalPreview } from '../components/CreatorPortalPreview';
import { useCampaign } from '../CampaignContext';
import { SidebarLayout } from '../../SettingsPage/components/SidebarLayout';

interface Props {
  mode?: 'add' | 'edit';
  project?: TProject;
  onSave?: () => void;
  refetchProjects?: () => void;
}

export const WorkflowTab: React.FC<Props> = ({
  mode = 'edit',
  project,
  onSave,
  refetchProjects,
}) => {
  const {
    data: {
      worklets: defaultWorklets = undefined,
    } = {},
    loading: isWorkletsLoading,
    refetch: refetchWorklets,
  } = useGetAllWorkletsForParentSpecQuery({
    variables: {
      specKey: project?.specKey,
    },
    skip: !project?.specKey,
    notifyOnNetworkStatusChange: true,
  });

  // Initialize campaign data
  useInitializeCampaign(mode, project);

  const [hasInitOnce, setHasInitOnce] = useState(false);

  const { worklets, setTasksConfig } = useCampaign();

  const { creatorTasksConfigs, isCreatorTasksConfigLoading } = useGetCreatorTasksConfig({
    variables: { programId: project?.id },
  });

  const { data: customStages, loading: isCustomStagesLoading, refetch: refetchCustomStages } = useGetCustomStages({
    variables: { programId: project?.id },
    skip: !project?.id,
  });

  const initTasks = useCallback((newWorklets?: GetAllWorkletsForParentSpecQuery_worklets[]) => {
    // Include ALL worklets - don't filter out hidden ones, let UI show "Hidden" badge instead
    const filteredWorklets = newWorklets || worklets;

    if (!filteredWorklets?.length) {
      return;
    }

    const taskConfigs = filteredWorklets?.map((worklet) => {
      const taskConfig = creatorTasksConfigs?.find((task) => task.specURI === worklet.specURI);
      if (taskConfig) {
        return {
          id: taskConfig.id,
          specURI: taskConfig.specURI,
          isInitial: taskConfig.isInitial,
          order: taskConfig.order,
          specTitle: worklet.specTitle,
        };
      }
      return {
        specURI: worklet.specURI,
        isInitial: true,
        order: 999, // Default order for unconfigured tasks
        specTitle: worklet.specTitle,
      };
    }).sort((a, b) => (a.order || 0) - (b.order || 0))
      // To normalize the order of the tasks
      .map((task, index) => ({
        ...task,
        order: index,
      }));

    setTasksConfig(taskConfigs || []);
    setHasInitOnce(true);
  }, [creatorTasksConfigs, worklets, setTasksConfig]);

  useEffect(() => {
    if (!isCreatorTasksConfigLoading && !isCustomStagesLoading && !hasInitOnce) {
      initTasks();
    }
  }, [isCreatorTasksConfigLoading, isCustomStagesLoading, initTasks, hasInitOnce]);

  const sections = [
    {
      id: 'workflow-stages',
      label: 'Workflow Stages',
      component: (
        <div className="flex justify-between">
          <ConsolidatedWorkflowSection
            mode={mode}
            project={project}
            defaultWorklets={defaultWorklets as TWorklet[]}
            parentSpecKey={project?.specKey}
            projectId={project?.id}
            onSave={() => {
              onSave?.();
              refetchWorklets?.();
            }}
            refetchWorklets={refetchWorklets}
            refetchProjects={refetchProjects}
            isCreatorTasksConfigLoading={isCreatorTasksConfigLoading || isCustomStagesLoading}
            initTasks={initTasks}
            customStages={customStages?.getAllStages || []}
            refetchCustomStages={refetchCustomStages}
          />
        </div>
      ),
      rightSection: (
        <div className="hidden lg:block lg:w-[465px] lg:min-w-[465px] border-l border-grey-3 h-full overflow-y-auto">
          <CreatorPortalPreview
            project={project}
            stages={worklets as TWorklet[]}
            isLoading={isWorkletsLoading || isCustomStagesLoading}
            customStages={customStages?.getAllStages || []}
          />
        </div>
      ),
    },
  ];

  return (
    <SidebarLayout
      sections={sections}
      activeSectionId="workflow-stages"
      onlyShowActiveSection
    />
  );
};
